import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { Alert, AlertTitle, Box, Button, Card, CardContent, Stack, Typography } from "@mui/material"
import { BoundAutocomplete, BoundStandardSwitch } from "lib/@components/binding/bound-components"
import { ListItemBox } from "lib/@components/ListItemBox"
import { PagedTable } from "lib/@components/paging/paged-table"
import { getRegisterDataForRegimeTable } from "./controller/get-register-data-for-regime-table"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { AssetRegisterRowEnd, AssetRegisterRowMiddle } from "slot-definitions"
import { RegimeSelectorRow } from "./regime-selector-row"
import { assetRegisterTableStyles } from "lib/@components/paging/paged-table/two-tone/asset-register-table-styles"
import { AddDateColumn, AddSyncColumn } from "./register-table-additional-columns"
import { getRegisterTableHeaders } from "./register-table-headers"
import { NoResults } from "lib/@components/no-results/no-results"
import { RegimeStructureSelector } from "./regime-structure-selector"
import { Bound } from "lib/@components/binding/Bound"
import { useEffect, useState } from "react"
import Iconify from "minimals-template/components/Iconify"
import { confirm } from "lib/@dialogs/confirm"
import { createRegimeFromRegister } from "routes/asset-register/controller/create-regime-from-register"
import { setAssetRegistriesForRegime } from "./controller/set-asset-registries.controller"
import { busyWhile } from "lib/@components/busy/busy-while"
import { assetRegisters } from "routes/asset-register/controller/asset-registers"
import { keyBy } from "lib/keyBy"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import { RegimeDraftLockHide } from "../regime-draft-locking/regime-draft-lock-hide"
import { If } from "lib/@components/switch"

export function RegimeSelector() {
    const { regime } = useBoundContext()
    const { synchronisationType: currentRegimeStructure } = regime.$
    const [current, setCurrent] = useBoundValue("current", undefined, { noChangeNotification: true })
    const [registers] = useBoundValue("registers")
    const [hasRegisters, setHasRegisters] = useState(false)
    const [syncType, setSyncType] = useState(currentRegimeStructure ?? "spaces")
    const allRegisters = keyBy(assetRegisters.useResults({ take: 1000, sort: "name" })?.items ?? [], (c) => c.id)
    AssetRegisterRowMiddle.usePlug(<AddDateColumn />)
    AssetRegisterRowEnd.usePlug(<AddSyncColumn />)
    useEffect(() => {
        setSyncType(currentRegimeStructure ?? syncType)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentRegimeStructure])

    const headers = getRegisterTableHeaders()

    return (
        <Bound
            syncType={syncType}
            setSyncType={setSyncType}
            allRegisters={allRegisters}
            registers={registers}
            currentRegimeStructure={currentRegimeStructure}
        >
            <Stack mt={2} spacing={4}>
                <Secure hide demand="syncAssetRegister" fallback={<Frag />}>
                    <RegimeDraftLockHide>
                        <Card sx={{ backgroundColor: "background.neutral", boxShadow: 0 }}>
                            <CardContent>
                                <Typography variant="subtitle1" pb={3}>
                                    Add Asset Register
                                </Typography>
                                <Box sx={{ display: "flex", direction: "row" }}>
                                    <ListItemBox data-cy="register-selector">
                                        <BoundAutocomplete
                                            isOptionEqualToValue={(option, value) =>
                                                (!value && true) || option === value
                                            }
                                            field="current"
                                            sx={{ minWidth: "20em", flex: 1, mr: 2 }}
                                            label="Select Asset Register"
                                            options={Object.keys(allRegisters).filter((c) => !registers.includes(c))}
                                            getOptionLabel={(id) => allRegisters[id]?.name ?? ""}
                                        />
                                    </ListItemBox>
                                    <Button
                                        variant={"contained"}
                                        onClick={addRegister}
                                        disabled={!current}
                                        sx={{
                                            minWidth: "fit-content",
                                            flexWrap: "no-wrap",
                                            "&.Mui-disabled": { backgroundColor: "action.disabledBackground" },
                                        }}
                                        startIcon={<Iconify icon="ic:round-plus" />}
                                        data-cy="add-register-to-regime-btn"
                                    >
                                        Add Asset Register
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </RegimeDraftLockHide>
                </Secure>
                {!registers || !registers.length ? (
                    <NoResults>{"There are no registers associated with this regime"}</NoResults>
                ) : (
                    <>
                        <Secure hide demand="syncAssetRegister" fallback={<Frag />}>
                            <RegimeStructureSelector />
                            <If value={hasRegisters}>
                                <ListItemBox mt={1} spacing={1}>
                                    <BoundStandardSwitch
                                        sideEffects
                                        field="isUnlocked"
                                        label="Allow manual regime edits"
                                        data-cy="manual-edit-switch"
                                    />
                                    <If field="isUnlocked">
                                        <Alert severity="warning" sx={{ flex: 1 }}>
                                            <AlertTitle>Editable Synchronised Regime</AlertTitle>
                                            <p>
                                                The regime can be manually edited, synchronising data from asset
                                                registers could replace or delete any manual edits made to the regime.
                                            </p>
                                        </Alert>
                                    </If>
                                </ListItemBox>
                            </If>
                        </Secure>
                        <PagedTable
                            onResults={handleResults}
                            headers={headers}
                            query={getRegisterDataForRegimeTable}
                            queryParams={{ regimeId: regime.id }}
                            RowComponent={RegimeSelectorRow}
                            tableSx={assetRegisterTableStyles}
                            searchable={false}
                            top={true}
                        />
                    </>
                )}
            </Stack>
        </Bound>
    )

    function handleResults({ totalCount }) {
        setHasRegisters(totalCount > 0)
    }

    async function addRegister() {
        const isFirstRegister = !registers || !registers.length
        const regimeHasManuallyAddedContent = !registers.length && regime.children.length
        if (current) {
            if (
                await confirm(
                    <>
                        <Typography gutterBottom>
                            This register will be automatically synchronised with the regime{" "}
                            {isFirstRegister ? "by asset location" : "using the method already selected"}.
                        </Typography>
                        {regimeHasManuallyAddedContent ? (
                            <Typography>
                                Manually added content in this regime will be overwritten. Further content can only be
                                added by synchronising more registers. <strong>This is irreversible</strong>.
                            </Typography>
                        ) : (
                            ""
                        )}
                    </>,
                    "Add register?"
                )
            ) {
                await busyWhile(async () => {
                    setCurrent(null)
                    await setAssetRegistriesForRegime(regime.id, [...registers, current])
                    await createRegimeFromRegister(current, regime._id, syncType)
                }, "Building Regime")
            }
        }
    }
}
